import PropTypes from 'prop-types';
import { config } from 'config';
import { documentTypeOptions } from 'shared/fixtures';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import omit from 'lodash/omit';
import { turnsActions } from 'core/Turns';
import { useTranslation } from 'react-i18next';
import {
    ActionContainer,
    AuditItem,
    Col,
    Container,
    DataContainer,
    DisplayErrorMessage,
    PatientCol,
    Row,
    Subtitle,
    TurnTypeDisplay
} from './styled';
import {
    AppointmentTypePicker,
    CompanyPicker,
    CoveragePicker,
    GenderPicker,
    PatientPicker,
    PlanPicker,
    PositionPicker,
    WorkplacePicker
} from 'views/pickers';
import {
    ButtonFooter,
    Card,
    Checkbox,
    DateSelect,
    ExpandButton,
    FormGroup,
    Input,
    Loader,
    SelectSearch,
    SlidePanel,
    TabController
} from 'mol-ui';
import {
    ChatModal,
    ErrorMessage,
    NotificationModal,
    PhoneInput
} from 'views/components';
import React, { useEffect, useState } from 'react';
import {
    allowAgendaByAvailable,
    isTurnOverlaping
} from '../../common';
import {
    dateToObject,
    identificationFormat,
    objectToDate,
    parseAppointmentStatusLabel,
    parseAppointmentType,
    parseGender
} from 'shared/utils';
import {
    useCheckbox,
    useDispatch,
    useInput,
    useSelector,
    useToggle,
    useWindowSize
} from 'hooks';
const {
    admission,
    company,
    coverages,
    patientIdentificationAlpha,
    patientMinimumFields,
    positions,
    turnOvershift,
    turnsEdit,
    turnsPatientCreation,
    turnsPresentialFlow,
    workplaces
} = config;

const CLEAN_PATIENT = {
    cellphone_number: '',
    email: '',
    enabled: true,
    first_name: '',
    gender: 'Female',
    health_insurance: '',
    identification: '',
    identification_id: 1,
    last_name: '',
    medical_coverage_id: 0
};

const TurnSlider = ({
    active = false,
    agenda,
    doctor = {},
    initialDate,
    mode = 'create',
    office = {},
    onCancel = () => null,
    onSuccess = () => null,
    specialty = {},
    turnId,
    turnList = []
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { xs } = useWindowSize();
    const {
        audit,
        auditLoading,
        creationError,
        selectedTurn = {},
        selectedTurnLoading,
        status = ''
    } = useSelector('turns');

    const turnType = !office || !office?.virtual ? 'presential' : 'online';
    const {
        messages,
        payment_status: turnPaymentStatus,
        status: turnStatus
    } = selectedTurn ?? {};
    const [appointmentType, setAppointmentType] = useState({});
    const [chatModal, toggleChatModal] = useToggle(false);
    const [commentsInput, comment, setComments] = useInput('', {
        fontSize: '13px',
        height: '100px',
        width: '100%'
    }, () => clearErrors('comment'));
    const [dateInput, date, setDate] = useInput(dateToObject(moment()));
    const [errors, setErrors] = useState({});
    const [existingPatient, setExistingPatient] = useState(true);
    const [modal, setModal] = useState('');
    const [newPatient, setNewPatient] = useState({ ...CLEAN_PATIENT });
    const [overshiftCheck, overshift, setOvershift] = useCheckbox(false, { label: t('Sobreturno') });
    const [patient, setPatient] = useState({});
    const [patientUrl, setPatientUrl] = useState('');
    const [timeInput, time, setTime] = useInput('', {}, () => clearErrors('time'));
    const [turnAlert, setTurnAlert] = useState('');

    useEffect(() => {
        if (mode === 'create') {
            setAppointmentType({});
            setComments('');
            setDate(dateToObject(initialDate));
            setErrors({});
            setOvershift(false);
            setPatient({});
            setTime(initialDate.format('HH:mm'));
        }
    }, [initialDate, mode, setAppointmentType, setComments, setDate, setOvershift, setTime]);

    useEffect(() => {
        if (mode === 'edit' && !isEmpty(selectedTurn)) {
            const {
                appointment_type,
                from_date_time,
                comment: turnComment,
                overshift: currentOvershift,
                patient: turnPatient,
                patient_url
            } = selectedTurn;
            setAppointmentType(appointment_type);
            setComments(turnComment ?? '');
            setDate(dateToObject(moment(from_date_time)));
            setExistingPatient(true);
            setErrors({});
            setOvershift(!!currentOvershift);
            setPatient(turnPatient);
            setPatientUrl(patient_url);
            setTime(moment(from_date_time).format('HH:mm'));
            dispatch(turnsActions.getTurnAudit(turnId));
        }
        if (mode === 'create') {
            dispatch(turnsActions.setTurnsAudit([]));
        }
    }, [mode, selectedTurn, setAppointmentType, setDate, setComments, setOvershift, setPatient, setTime]);

    useEffect(() => {
        if (!allowAgendaByAvailable(moment(`${ objectToDate(date).format('YYYY-MM-DD') } ${ time }`), agenda)) {
            setTurnAlert(t('¡Cuidado! El horario elegido no se encuentra disponible para dar el turno'));
        } else if (!isEmpty(date) && !!time && !isEmpty(appointmentType) && isTurnOverlaping(
            moment(`${ objectToDate(date).format('YYYY-MM-DD') } ${ time }`, 'YYYY-MM-DD HH:mm'),
            moment(`${ objectToDate(date).format('YYYY-MM-DD') } ${ time }`, 'YYYY-MM-DD HH:mm').add(appointmentType.duration, 'minutes'),
            turnList.filter(({ resource: { id, overshift } }) => !overshift && id !== turnId))) {
            setTurnAlert(t('Ya existe otro turno para el horario seleccionado'));
        } else {
            setTurnAlert('');
        }
    }, [agenda, appointmentType, date, setTurnAlert, time, turnId, turnList]);

    useEffect(() => {
        if (status === 'created') {
            dispatch(turnsActions.setTurnsStatus(''));
            clearForm();
            onSuccess();
        }
    }, [onSuccess, status]);

    useEffect(() => {
        if (turnId && active) {
            dispatch(turnsActions.getSelectedTurn({ id: turnId, type: 'turn' }));
        } else {
            dispatch(turnsActions.setTurnsSelectedTurn(null));
        }
    }, [active, turnId]);

    const handleCancel = () => {
        const data = {
            cancellation_reason: t('Cancelado por el administrador'),
            id: turnId
        };
        dispatch(turnsActions.cancelTurn(data));
        setModal('');
        closeModal();
    };

    const clearForm = () => {
        setAppointmentType({});
        setComments('');
        setDate(dateToObject(moment()));
        setErrors({});
        setExistingPatient(true);
        setModal('');
        setNewPatient({ ...CLEAN_PATIENT });
        setOvershift(false);
        setPatient({});
        setTime('');
        setTurnAlert('');
    };

    const closeModal = () => {
        dispatch(turnsActions.setTurnsCreationError({}));
        onCancel();
    };

    const clearErrors = error => {
        delete errors[error];
        setErrors(errors);
    };

    const handleAppointmentType = value => {
        clearErrors('appointmentType');
        setAppointmentType(value);
    };

    const handleNewPatientChange = (id, value) => {
        clearErrors(id);
        setNewPatient({ ...newPatient, [id]: value });
        if (!isEmpty(creationError) && !!creationError[id]) {
            dispatch(turnsActions.setTurnsCreationError(omit(creationError, id)));
        }
    };

    const handlePatientChange = value => {
        clearErrors('patient');
        setPatient(value);
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const commonProps = {
            comment: comment || '',
            day: objectToDate(date).format('YYYY-MM-DD'),
            from_date_time: moment(`${ objectToDate(date).format('YYYY-MM-DD') } ${ time }`).format(),
            from_time: time,
            office_id: office?.id,
            overshift,
            type: turnType,
            type_id: appointmentType.id
        };
        if (mode === 'create') {
            const newPatientData = { ...newPatient };
            delete newPatientData.birth_date;
            delete newPatientData.medical_coverage_id;
            delete newPatientData.plan_id;
            const data = {
                ...commonProps,
                doctor_id: doctor?.id,
                newPatient: existingPatient ? null : {
                    ...newPatientData,
                    ...(!!newPatient.birth_date && { birth_date: objectToDate(newPatient.birth_date).format('YYYY-MM-DD') }),
                    enabled: newPatient.enabled ? 1 : 0,
                    ...(!!newPatient.medical_coverage_id && { medical_coverage_id: newPatient.medical_coverage_id }),
                    ...(!!newPatient.plan_id && { plan_id: newPatient.plan_id })
                },
                patient_id: patient.id,
                specialty_id: specialty?.id
            };
            dispatch(turnsActions.createTurn(data));
        } else if (mode === 'edit') {
            const data = {
                ...commonProps,
                id: turnId
            };
            dispatch(turnsActions.editTurn(data));
            onSuccess();
        }
    };
    
    const paymentLabel = paymentStatus => {
        switch (paymentStatus) {
        case ('completed'): return ` - ${ t('Abonado') }`;` - ${ t('') }`;
        case ('completed_by_admin'): return ` - ${ t('Administrador') }`;
        case ('completed_by_doctor'): return ` - ${ t('Profesional') }`;
        case ('coverage'): return ` - ${ t('Validado con cobertura') }`;
        default: return '';
        }
    };

    const changeStatus = (status, key = 'status') => {
        const data = {
            id: turnId,
            [key]: status
        };
        dispatch(turnsActions.editTurn(data));
        setModal('');
        closeModal();
    };

    const validate = () => {
        const baseErrors = {
            ...(!appointmentType?.id && { appointmentType: t('Requerido') }),
            ...((isEmpty(patient) && existingPatient) && { patient: t('Requerido') }),
            ...(!time && { time: t('Requerido') })
        };
        const newPatientErrors = !existingPatient && {
            ...(!newPatient.first_name && { first_name: t('Requerido') }),
            ...(!newPatient.identification && { identification: t('Requerido') }),
            ...(!newPatient.last_name && { last_name: t('Requerido') })
        };
        const optionalPatientErrors = !existingPatient && !patientMinimumFields && {
            ...(!newPatient.birth_date && { birth_date: t('Requerido') }),
            ...(newPatient.birth_date && !every(newPatient.birth_date) && { birth_date: t('Fecha inválida') }),
            ...(!newPatient.cellphone_number && { cellphone_number: t('Requerido') }),
            ...(!newPatient.email && { email: t('Requerido') })
        };
        const newErrors = { ...baseErrors, ...newPatientErrors, ...optionalPatientErrors };
        setErrors(newErrors);
        return isEmpty(newErrors);
    };

    const modalController = {
        absent: {
            action: () => changeStatus('absent'),
            title: t('¿Desea marcar el turno como ausente?')
        },
        admitted: {
            action: () => changeStatus('admitted'),
            title: t('¿Desea admitir al paciente?')
        },
        cancel: {
            action: handleCancel,
            title: t('¿Desea cancelar el turno')
        },
        completed: {
            action: () => changeStatus('completed', 'payment_status'),
            title: t('¿Desea marcar como pagado el turno Esta acción no se puede deshacer')
        },
        finish: {
            action: () => changeStatus('finalized'),
            title: t('¿Desea marcar el turno como atendido?')
        },
        initial: {
            action: () => changeStatus('initial'),
            title: t('¿Desea volver a marcar el turno como pendiente?')
        },
        inroom: {
            action: () => changeStatus('inroom'),
            title: t('¿Desea ingresar al paciente')
        }
    };

    const { action = () => null, title = '' } = !!modal ? modalController[modal] : {};
    const panelTitle = `${ mode === 'create' ? t('Creando') : t('Editando') } ${ t('turno') }`;
    const editMode = mode === 'edit';
    const isFinalizedTurn = turnStatus === 'finalized';
    const showSubmitButton = !isFinalizedTurn && (mode === 'create' || (editMode && turnsEdit));

    return (
        <SlidePanel
            active={ active }
            loading={ selectedTurnLoading }
            title={ panelTitle }
            onToggle={ closeModal }
        >
            <ChatModal
                active={ chatModal }
                messages={ messages }
                onToggle={ toggleChatModal }
            />
            <NotificationModal
                active={ !!modal }
                caption={ t('Aceptar') }
                height="250px"
                title={ title }
                onBack={ () => setModal('') }
                onSubmit={ action }
            />
            <Container>
                {
                    editMode &&
                    <Subtitle>
                        { parseAppointmentStatusLabel(turnStatus) ?? '' }{ paymentLabel(turnPaymentStatus) ?? '' }
                    </Subtitle>
                }
                {
                    doctor?.id &&
                    <Subtitle>
                        { t('Profesional') }: { doctor?.name ?? '-' }
                    </Subtitle>
                }
                {
                    !doctor?.id && !!specialty?.id &&
                    <Subtitle>
                        { t('Especialidad') }: { specialty?.name ?? '-' }
                    </Subtitle>
                }
                {
                    !!patientUrl &&
                    <Subtitle>
                        { t('URL') }: { patientUrl }
                    </Subtitle>
                }
                <Subtitle margin="5px 0px 20px 0px">
                    { t('Consultorio') }: { office?.name ?? '-' }
                </Subtitle>
                <Row>
                    <Col padding="0px" sm="8" xs="12">
                        <DateSelect
                            disabled={ (editMode && !turnsEdit) || isFinalizedTurn }
                            nextYears={ 1 }
                            { ...dateInput }
                        />
                    </Col>
                    <Col padding="0px" sm="4" xs="12">
                        <Input
                            disabled={ (editMode && !turnsEdit) || isFinalizedTurn }
                            error={ !!errors.time }
                            margin={ xs ? '10px 0px' : '0px 0px 10px 10px' }
                            type="time"
                            width="100px"
                            { ...timeInput }
                        />
                        <ErrorMessage relative error={ errors.time } marginLeft={ 0 } />
                    </Col>
                </Row>
                <Card margin="0px 0px 0px 0px" width="100%">
                    <Row>
                        <Col sm="6" xs="12">
                            <TurnTypeDisplay>
                                { t('Turno') } { parseAppointmentType(turnType) }
                            </TurnTypeDisplay>
                        </Col>
                        <Col sm="6" xs="12">
                            <AppointmentTypePicker
                                objectChange
                                disabled={ (editMode && !turnsEdit) || isFinalizedTurn }
                                doctorId={ doctor?.id }
                                specialtyId={ specialty?.id }
                                type={ turnType }
                                value={ appointmentType?.id }
                                onChange={ handleAppointmentType }
                            />
                            <ErrorMessage relative error={ errors.appointmentType } marginLeft={ 0 } />
                        </Col>
                    </Row>
                </Card>
                <Card margin="10px 0px" padding="0px 10px 10px 10px" width="100%">
                    {
                        mode === 'create' && turnsPatientCreation &&
                        <TabController
                            borderless
                            items={ [{
                                key: 'existing',
                                label: !xs ? t('Paciente existente') : t('Existente')
                            }, {
                                key: 'new',
                                label: !xs ? t('Nuevo Paciente') : t('Nuevo')
                            }] }
                            margin="0px"
                            value={ existingPatient ? 'existing' : 'new' }
                            onChange={ key => setExistingPatient(key === 'existing') }
                        />
                    }
                    {
                        existingPatient &&
                        <Card margin="10px 0px" width="100%">
                            {
                                mode === 'create' &&
                                <Row>
                                    <Col xs="12">
                                        <PatientPicker
                                            objectChange
                                            fontSize="14px"
                                            value={ patient.id || null }
                                            width="100%"
                                            onChange={ handlePatientChange }
                                        />
                                        <ErrorMessage relative error={ errors.patient } />
                                    </Col>
                                </Row>
                            }
                            {
                                !isEmpty(patient) &&
                                <Row>
                                    <PatientCol md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Nombre') }>
                                            { patient.last_name }, { patient.first_name }
                                        </FormGroup>
                                    </PatientCol>
                                    <PatientCol md="4" sm="6" xs="12">
                                        <FormGroup label={ t('DNI') }>
                                            { identificationFormat(patient.identification) }
                                        </FormGroup>
                                    </PatientCol>
                                    <PatientCol md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Género') }>
                                            { parseGender(patient.gender) }
                                        </FormGroup>
                                    </PatientCol>
                                    <PatientCol md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Fecha de nacimiento') }>
                                            { !!patient.birth_date ? moment(patient.birth_date).format('DD/MM/YYYY') : '-' }
                                        </FormGroup>
                                    </PatientCol>
                                    <PatientCol md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Email') }>
                                            { patient.email || '-' }
                                        </FormGroup>
                                    </PatientCol>
                                    <PatientCol md="4" sm="6" xs="12">
                                        <FormGroup label={ t('Celular') }>
                                            { patient.cellphone_number || '-' }
                                        </FormGroup>
                                    </PatientCol>
                                    {
                                        company &&
                                        <PatientCol md="4" sm="6" xs="12">
                                            <FormGroup label={ t('Empresa') }>
                                                { patient?.company?.name || '-' }
                                            </FormGroup>
                                        </PatientCol>
                                    }
                                    {
                                        workplaces &&
                                        <PatientCol md="4" sm="6" xs="12">
                                            <FormGroup label={ t('Lugar de trabajo') }>
                                                { patient?.workplace?.name || patient?.workplace || '-' }
                                            </FormGroup>
                                        </PatientCol>
                                    }
                                    {
                                        positions &&
                                        <PatientCol md="4" sm="6" xs="12">
                                            <FormGroup label={ t('Cargo') }>
                                                { patient?.position?.name || patient?.position || '-' }
                                            </FormGroup>
                                        </PatientCol>
                                    }
                                    {
                                        coverages &&
                                        <>
                                            <PatientCol md="4" sm="6" xs="12">
                                                <FormGroup label={ t('Cobertura') }>
                                                    { patient?.medical_coverage?.name ?? '-' }
                                                </FormGroup>
                                            </PatientCol>
                                            <PatientCol md="4" sm="6" xs="12">
                                                <FormGroup label={ t('Plan') }>
                                                    { patient?.plan?.name ?? '-' }
                                                </FormGroup>
                                            </PatientCol>
                                            <PatientCol md="4" sm="6" xs="12">
                                                <FormGroup label={ t('Afiliado') }>
                                                    { patient.health_insurance || '-' }
                                                </FormGroup>
                                            </PatientCol>
                                        </>
                                    }
                                </Row>
                            }
                        </Card>
                    }
                    {
                        !existingPatient &&
                        <DataContainer>
                            <Row>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Apellido') }>
                                        <Input
                                            error={ errors?.last_name }
                                            margin="0px"
                                            value={ newPatient.last_name }
                                            onChange={ value => handleNewPatientChange('last_name', value) }
                                        />
                                    </FormGroup>
                                    <ErrorMessage relative error={ errors.last_name } marginLeft={ 0 } />
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Nombre') }>
                                        <Input
                                            error={ errors?.first_name }
                                            margin="0px"
                                            value={ newPatient.first_name }
                                            onChange={ value => handleNewPatientChange('first_name', value) }
                                        />
                                    </FormGroup>
                                    <ErrorMessage relative error={ errors.first_name } marginLeft={ 0 } />
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Tipo de documento') }>
                                        <SelectSearch
                                            margin="0px"
                                            options={ documentTypeOptions }
                                            value={ newPatient.identification_id }
                                            width="100%"
                                            onChange={ value => handleNewPatientChange('identification_id', value) }
                                        />
                                    </FormGroup>
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('DNI') }>
                                        <Input
                                            error={ errors.identification ?? creationError.identification }
                                            margin="0px"
                                            type={ patientIdentificationAlpha ? 'text' : 'number' }
                                            value={ newPatient.identification }
                                            onChange={ value => handleNewPatientChange('identification', value) }
                                        />
                                    </FormGroup>
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Fecha de Nac.') }>
                                        <DateSelect
                                            error={ errors.birthDate }
                                            margin="0px"
                                            value={ newPatient.birth_date }
                                            onChange={ value => handleNewPatientChange('birth_date', value) }
                                        />
                                    </FormGroup>
                                    <ErrorMessage relative error={ errors.birth_date } marginLeft={ 0 } />
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Email') }>
                                        <Input
                                            error={ errors.email ?? creationError.email }
                                            margin="0px"
                                            value={ newPatient.email }
                                            onChange={ value => handleNewPatientChange('email', value) }
                                        />
                                    </FormGroup>
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Teléfono') }>
                                        <PhoneInput
                                            value={ newPatient.cellphone_number ? newPatient.cellphone_number : '+54' }
                                            onChange={ value => handleNewPatientChange('cellphone_number', value) }
                                        />
                                    </FormGroup>
                                    <ErrorMessage relative error={ errors.cellphone_number } marginLeft={ 0 } />
                                </PatientCol>
                                <PatientCol sm="6" xs="12">
                                    <FormGroup label={ t('Género') }>
                                        <GenderPicker
                                            margin="0px 0px 10px 0px"
                                            value={ newPatient.gender || 'Female' }
                                            onChange={ value => handleNewPatientChange('gender', value) }
                                        />
                                    </FormGroup>
                                </PatientCol>
                                {
                                    company &&
                                    <PatientCol sm="6" xs="12">
                                        <FormGroup label={ t('Empresa') }>
                                            <CompanyPicker
                                                value={ newPatient.company_id }
                                                onChange={ value => handleNewPatientChange('company_id', value) }
                                            />
                                        </FormGroup>
                                    </PatientCol>
                                }
                                {
                                    workplaces &&
                                    <PatientCol sm="6" xs="12">
                                        <FormGroup label={ t('Lugar de trabajo') }>
                                            <WorkplacePicker
                                                value={ newPatient.workplace_id }
                                                onChange={ value => handleNewPatientChange('workplace_id', value) }
                                            />
                                        </FormGroup>
                                    </PatientCol>
                                }
                                {
                                    positions &&
                                    <PatientCol sm="6" xs="12">
                                        <FormGroup label={ t('Cargo') }>
                                            <PositionPicker
                                                allowNone
                                                value={ newPatient.position_id }
                                                onChange={ value => handleNewPatientChange('position_id', value) }
                                            />
                                        </FormGroup>
                                    </PatientCol>
                                }
                            </Row>
                            {
                                coverages &&
                                <>
                                    <Row>
                                        <PatientCol sm="6" xs="12">
                                            <FormGroup label={ t('Cobertura') }>
                                                <CoveragePicker
                                                    allowNone={ true }
                                                    value={ newPatient.medical_coverage_id }
                                                    onChange={ value => handleNewPatientChange('medical_coverage_id', value) }
                                                />
                                            </FormGroup>
                                        </PatientCol>
                                        <PatientCol sm="6" xs="12">
                                            <FormGroup label={ t('Plan') }>
                                                <PlanPicker
                                                    allowNone={ true }
                                                    params={ newPatient.medical_coverage_id }
                                                    value={ newPatient.plan_id }
                                                    onChange={ value => handleNewPatientChange('plan_id', value) }
                                                />
                                            </FormGroup>
                                        </PatientCol>
                                        <PatientCol sm="6" xs="12">
                                            <FormGroup label={ t('Afiliado') }>
                                                <Input
                                                    value={ newPatient.health_insurance }
                                                    onChange={ value => handleNewPatientChange('health_insurance', value) }
                                                />
                                            </FormGroup>
                                        </PatientCol>
                                    </Row>
                                </>
                            }
                            <Row>
                                <PatientCol sm="6" xs="12">
                                    <Checkbox
                                        checked={ newPatient.enabled }
                                        fontSize="13px"
                                        label={ t('Habilitado para ingresar a la plataforma') }
                                        onClick={ checked => handleNewPatientChange('enabled', checked) }
                                    />
                                </PatientCol>
                            </Row>
                        </DataContainer>
                    }
                </Card>
                <Row>
                    <Col padding="0px" xs="12">
                        <FormGroup label={ t('Comentarios') } padding="0px">
                            <Input
                                disabled={ (editMode && !turnsEdit) || isFinalizedTurn }
                                type="area"
                                { ...commentsInput }
                            />
                        </FormGroup>
                    </Col>
                </Row>
                {
                    !!turnOvershift &&
                    <Checkbox
                        disabled={ (editMode && !turnsEdit) || isFinalizedTurn }
                        { ...overshiftCheck }
                    />
                }
                {
                    editMode &&
                    <Row>
                        <Col padding="0px" xs="12">
                            <FormGroup label={ t('Acciones') } margin="20px 0px 0px 0px" padding="0px">
                                <Card margin="0px 0px 20px 0px">
                                    <DataContainer flexDirection="row" margin="0px" paddingBottom="10px">
                                        {
                                            ['initial'].includes(turnStatus) &&
                                            <>
                                                {
                                                    turnsPresentialFlow &&
                                                    <ActionContainer highlight onClick={ () => setModal('admitted') }>
                                                        { t('Admitir paciente') }
                                                    </ActionContainer>
                                                }
                                                <ActionContainer onClick={ () => setModal('cancel') }>
                                                    { t('Cancelar turno') }
                                                </ActionContainer>
                                                {
                                                    !turnsPresentialFlow &&
                                                    <ActionContainer onClick={ () => setModal('finish') }>
                                                        { t('Marcar como atendido') }
                                                    </ActionContainer>
                                                }
                                                <ActionContainer onClick={ () => setModal('absent') }>
                                                    { t('Paciente ausente') }
                                                </ActionContainer>
                                            </>
                                        }
                                        {
                                            ['admitted'].includes(turnStatus) && turnsPresentialFlow &&
                                            <ActionContainer highlight onClick={ () => setModal('inroom') }>
                                                { t('Ingresar a consultorio') }
                                            </ActionContainer>
                                        }
                                        {
                                            ['admitted', 'initial', 'inroom'].includes(turnStatus) && turnsPresentialFlow &&
                                            <ActionContainer highlight={ turnStatus === 'inroom' } onClick={ () => setModal('finish') }>
                                                { t('Marcar como atendido') }
                                            </ActionContainer>
                                        }
                                        {
                                            ['absent', 'admitted', 'cancelled', 'finalized', 'inroom'].includes(turnStatus) &&
                                            <ActionContainer onClick={ () => setModal('initial') }>
                                                { t('Volver a marcar como pendiente') }
                                            </ActionContainer>
                                        }
                                        {
                                            !['completed', 'completed_by_admin', 'completed_by_doctor'].includes(turnPaymentStatus) &&
                                            <ActionContainer onClick={ () => setModal('completed') }>
                                                { t('Marcar como pagado') }
                                            </ActionContainer>
                                        }
                                        {
                                            !isEmpty(messages) && admission &&
                                            <ActionContainer onClick={ toggleChatModal }>
                                                { t('Ver historial de chat') }
                                            </ActionContainer>
                                        }
                                    </DataContainer>
                                </Card>
                            </FormGroup>
                        </Col>
                    </Row>
                }
                {
                    !!turnAlert && mode !== 'edit' &&
                    <Row>
                        <Col centered="true">
                            <DisplayErrorMessage>
                                { turnAlert }
                            </DisplayErrorMessage>
                        </Col>
                    </Row>
                }
                <Loader active={ auditLoading } />
                {
                    mode !== 'create' && !auditLoading && !isEmpty(audit) &&
                    <Card
                        margin="10px 0px"
                        maxHeight="200px"
                        overflowY="auto"
                        padding="10px 5px 0px 5px"
                        title={ t('Historial') }
                        width="100%"
                    >
                        {
                            audit.map(({
                                action,
                                additional_information,
                                datetime,
                                user
                            }, index) =>
                                <AuditItem key={ index }>
                                    { `${ moment(datetime).format('DD/MM/YYYY HH:mm:ss') }` }
                                    { ` | ${ action } | ` }
                                    { user }
                                    { additional_information ? ` | ${ additional_information }` : '' }
                                </AuditItem>
                            )
                        }
                    </Card>
                }
                <ButtonFooter margin="20px 0px 10px 0px">
                    <ExpandButton
                        secondary
                        caption={ t('Volver') }
                        size={ 100 }
                        onClick={ closeModal }
                    />
                    {
                        showSubmitButton &&
                        <ExpandButton
                            caption={ mode === 'create' ? t('Crear') : t('Editar') }
                            danger={ !!turnAlert }
                            loading={ status === 'creating' }
                            size={ 100 }
                            onClick={ handleSubmit }
                        />
                    }
                </ButtonFooter>
            </Container>
        </SlidePanel>
    );
};

TurnSlider.propTypes = {
    active: PropTypes.bool,
    agenda: PropTypes.object,
    calendarData: PropTypes.object,
    doctor: PropTypes.object,
    initialDate: PropTypes.object,
    mode: PropTypes.string,
    office: PropTypes.object,
    onCancel: PropTypes.func,
    onSuccess: PropTypes.func,
    specialty: PropTypes.object,
    turnId: PropTypes.number,
    turnList: PropTypes.array
};

export default TurnSlider;
